import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Gallery from "../../../../components/Gallery/Gallery";
import AttachmentApi from "../../../../api/services/AttachmentApi";
import AttachmentFileHandler from "../../../../helper/AttachmentFileHandler";
import TestSeriesApi, { TestSeriesAttachmentReference } from "../../../../api/services/TestSeriesApi";
import FileReference from "../../../../components/Gallery/model/FileReference.model";
import LoadedFile from "../../../../components/Gallery/model/LoadedFile.model";
import { GalleryContextProvider } from "../../../../contexts/GalleryContext";
import NamingStrategy from "../../../../components/Gallery/model/NamingStrategy";
import useEnv from "../../../../hooks/env";
import { TestSeriesContext } from "../../../../contexts/TestSeriesContext";
import { GeneralFileType } from "../../../../components/Gallery/GalleryFilter";
import TestRequestApi from "../../../../api/services/TestRequestApi";
import RequestsPagePathParams from "../../../../api/models/test-request/RequestsPagePathParams";
import CustomLoading from "../../../../components/Alert/CustomLoading";
import ErrorSnackbarContext from "../../../../contexts/ErrorSnackbarContext";

export interface TestSeriesGalleryProps {
  disabled: boolean;
}

type FileTypeFilter = keyof typeof GeneralFileType;
enum ZipNameByFileType {
  ALL = "Files",
  IMAGES = "Pictures",
  DOCUMENTS = "Documents",
}

const TestSeriesGallery: React.FC<TestSeriesGalleryProps> = (props) => {
  const { disabled } = props;
  const { api } = useEnv();
  const { testRequestId } = useParams<RequestsPagePathParams>();
  const { handleHttpError } = useContext(ErrorSnackbarContext);
  const { selectedTestSeriesObject, testSeriesCurrentVersion, originRequestId } = useContext(TestSeriesContext);
  const [loading, setLoading] = useState(false);
  const attachmentApi = new AttachmentApi(api);
  const testRequestApi = new TestRequestApi(api);
  const testSeriesApi = new TestSeriesApi(api);
  const attachmentFileHandler = new AttachmentFileHandler(
    attachmentApi,
    {
      remove: (idToBeDeleted) =>
        testSeriesApi
          .removeAttachmentFromTestSeries(selectedTestSeriesObject.id, idToBeDeleted)
          .then((attachments) => ({ attachments, idToBeDeleted })),
      append: () => Promise.reject(Error("not possible")),
      zipFileName(files: FileReference[], generalFilterType: FileTypeFilter): string {
        if (files.length === 1) {
          const file = files[0];
          if (file.status === "LOADED") {
            return file.name;
          }
          return file.id;
        }
        return `LEAP-${originRequestId || testRequestId}-${selectedTestSeriesObject.indexNumber}-${
          selectedTestSeriesObject.name
        }_${ZipNameByFileType[generalFilterType]}.zip`;
      },
    },
    () =>
      testRequestApi.getTestRequestAttachmentClearanceToken(originRequestId || testRequestId).then((res) => res.data),
  );

  const namingStrategy: NamingStrategy = {
    nameDetailDialog(file: LoadedFile) {
      return {
        header: `GALLERY ${testRequestId} Description`,
        subheader: file.name,
      };
    },
  };

  const [attachmentReferences, setAttachmentReferences] = useState<TestSeriesAttachmentReference[]>([]);
  useEffect(() => {
    setLoading(true);
    testSeriesApi
      .fetchAttachmentsToTestSeries(selectedTestSeriesObject.id, testSeriesCurrentVersion)
      .then((res) => {
        setAttachmentReferences(res.data);
      })
      .catch(handleHttpError)
      .finally(() => setLoading(false));
  }, [selectedTestSeriesObject.id, testSeriesCurrentVersion]);

  if (loading) {
    return <CustomLoading />;
  }

  return (
    <GalleryContextProvider>
      <>
        {attachmentReferences.length === 0 && "No files attached."}
        <Gallery
          altLayout
          filterable
          disabled={disabled}
          disableUploads
          galleryId={testRequestId}
          fileHandler={attachmentFileHandler}
          namingStrategy={namingStrategy}
          attachments={attachmentReferences}
        />
      </>
    </GalleryContextProvider>
  );
};

export default TestSeriesGallery;
